import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  EventEmitter,
  OnDestroy,
  OnInit,
  TemplateRef,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import type { Params } from '@angular/router';
import { RouterLink, RouterLinkActive, UrlTree } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  IconColorDirective,
  IconComponent,
  IconName,
  IconSize,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';

import { SidebarInternalService } from '../sidebar/sidebar-internal.service';

export enum ItemType {
  Heading = 'heading',
  Item = 'item',
}

export enum SidebarItemSize {
  Small,
  Default,
  Large,
}

type TItemType = `${ItemType}`;

@Component({
  selector: 'rt-sidebar-item',
  templateUrl: './sidebar-item.component.html',
  styleUrls: ['./sidebar-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.heading]': 'type() === ItemType.Heading',
    '[class.item]': 'type() === ItemType.Item',
    '[class.default]': 'itemSize() === SidebarItemSize.Default',
    '[class.small]': 'itemSize() === SidebarItemSize.Small',
    '[class.large]': 'itemSize() === SidebarItemSize.Large',
    '[class.align-start]': 'alignStart()',
  },
  imports: [
    IconColorDirective,
    IconComponent,
    NgTemplateOutlet,
    OverlayOriginDirective,
    RouterLink,
    RouterLinkActive,
    RtTooltip,
  ],
  animations: [
    trigger('activeAnimation', [
      transition('* => true', [
        style({
          transform: 'scale(0)',
          transformOrigin: 'top',
          height: '0',
        }),
        animate(
          '500ms ease-in-out',
          keyframes([
            style({ transform: 'scale(1)', height: '44px', offset: 0.9 }),
            style({ transform: 'scale(1)', height: '36px', offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
})
export class SidebarItemComponent implements OnInit, OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly sidebarService = inject(SidebarInternalService);
  public readonly elementRef = inject<ElementRef<Element>>(ElementRef);

  public readonly type = input<TItemType>(undefined!);
  public readonly name = input<string>(undefined!);
  public readonly icon = input<IconName>();
  public readonly active = input<boolean>(undefined!);
  public readonly activeAnimation = input<boolean>(undefined!);
  public readonly iconActive = input<boolean>(undefined!);
  public readonly textActive = input<boolean>(undefined!);

  public readonly leftSlotContext = input<Record<string, any> | null>();
  public readonly rightSlotContext = input<Record<string, any> | null>();
  public readonly bottomSlotContext = input<Record<string, any> | null>();
  public readonly draggable = input<boolean>(undefined!);
  public readonly dragIconSize = input<IconSize>('small');
  public readonly light = input<boolean>(undefined!);
  public readonly itemSize = input<SidebarItemSize>(SidebarItemSize.Default);
  public readonly hoverable = input<boolean>(true);
  public readonly tooltipEnabled = input<boolean>(true);
  public readonly activeStateIndicatorEnabled = input<boolean>(true);
  public readonly alignStart = input<boolean>(false);
  @Input() public leftSlot?: TemplateRef<any>;
  @Input() public rightSlot?: TemplateRef<any> | null;
  @Input() public bottomSlot?: TemplateRef<any> | null;

  @Input()
  public set href(value: UrlTree | string | undefined | null) {
    if (value instanceof UrlTree) {
      this.url = value.toString().split('?')[0];
      this.queryParams = value.queryParams;
    } else {
      this.url = value;
    }
  }

  /**
   * @see https://github.com/angular/angular/issues/19826
   */
  @Output() public activeAnimationDone = new EventEmitter<void>();

  @ViewChild('itemName') public itemNameRef: ElementRef<HTMLDivElement>;

  // TODO: consider removing, used only in search sidebar
  @ViewChild('rightOutletOrigin')
  public rightOutletOrigin: OverlayOriginDirective;

  public ItemType: typeof ItemType = ItemType;
  public SidebarItemSize: typeof SidebarItemSize = SidebarItemSize;
  public sidebarMinimized: boolean;
  public url?: string | null;
  public queryParams: Params;
  public isTooltipEnabledFn: () => boolean = ((): boolean => {
    if (!this.itemNameRef) return false;

    const { nativeElement } = this.itemNameRef;
    return nativeElement.scrollWidth > nativeElement.clientWidth;
  }).bind(this);

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.sidebarService.sidebarMinimized$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(isMinimized => {
        this.sidebarMinimized = isMinimized;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
